import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ProjectGrid from '../projects/ProjectGrid';
import ClientPortalNotes from './ClientPortalNotes';
import ProjectHeader from './ProjectHeader';
import DueDatesSummary from './DueDatesSummary';
import ClientPortalMissingItems from './ClientPortalMissingItems';
import StepDetailsModal from './StepDetailsModal';
import CentralFileUploader from './CentralFileUploader';
import ClientPortalProjectPlan from './ClientPortalProjectPlan';
import VideoPlayer from './VideoPlayer';

// Video Modal Component
const VideoModal = ({ isOpen, onClose, videoUrl, title, darkMode }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-800 bg-opacity-50" onClick={onClose}>
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose}></div>
        
        <div className="relative bg-gray-800 rounded-lg max-w-4xl w-full mx-auto shadow-xl overflow-hidden">
          <div className="p-4 border-b border-gray-700 flex justify-between items-center">
            <h3 className="text-lg font-medium text-white">{title}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-300">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Video container with explicit height */}
          <div className="w-full" style={{ height: "480px" }}>
            <VideoPlayer url={videoUrl} />
          </div>
          
          <div className="p-4 bg-gray-700 border-t border-gray-600">
            <p className="text-sm text-gray-300">
              This video provides instructions and information about this phase of the project.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const ClientDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clientData, setClientData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedStep, setSelectedStep] = useState(null);
  const [phaseStatusTypes, setPhaseStatusTypes] = useState([]);
  const [stepStatusTypes, setStepStatusTypes] = useState([]);
  const [activeTab, setActiveTab] = useState('timeline');
  const [showAllProjects, setShowAllProjects] = useState(false);
  
  // Video modal state
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [videoTitle, setVideoTitle] = useState('');
  
  useEffect(() => {
    // Check for client token
    const token = localStorage.getItem('clientToken');
    if (!token) {
      navigate('/client-login');
      return;
    }
    
    // Set up auth header for axios
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    
    // Load initial data
    fetchClientData();
    fetchStatusTypes();
  }, [navigate]);

  useEffect(() => {
    // If selected project changes and it has an ID, fetch complete project details 
    if (selectedProject?.id) {
      fetchProjectDetails(selectedProject.id);
    }
  }, [selectedProject?.id]);
  
  const fetchClientData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Verify token and get client data
      const verifyResponse = await axios.get('/api/client-auth/verify');
      
      if (!verifyResponse.data.valid) {
        localStorage.removeItem('clientToken');
        navigate('/client-login');
        return;
      }
      
      setClientData({
        client: verifyResponse.data.client,
        contact: verifyResponse.data.contact
      });
      
      // Fetch client projects
      console.log('Fetching list of client projects');
      const projectsResponse = await axios.get('/api/client-portal/projects');
      const projects = projectsResponse.data.projects || [];
      console.log(`Found ${projects.length} projects`);
      setProjects(projects);
      
      if (projects.length > 0) {
        console.log(`Selecting first project: ${projects[0].id} - ${projects[0].name}`);
        // Instead of just setting the first project, fetch its full details immediately
        try {
          const firstProjectId = projects[0].id;
          const detailsResponse = await axios.get(`/api/client-portal/projects/${firstProjectId}`);
          if (detailsResponse.data && detailsResponse.data.project) {
            const fullProject = detailsResponse.data.project;
            console.log(`Project ${fullProject.id} details retrieved. Phases: ${fullProject.phases?.length || 0}`);
            setSelectedProject(fullProject);
          } else {
            console.warn('No detailed project data found, using basic project info');
            setSelectedProject(projects[0]);
          }
        } catch (projectError) {
          console.error('Error fetching detailed project data:', projectError);
          // Fallback to basic project info
          setSelectedProject(projects[0]);
        }
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Failed to load client data:', error);
      setError('Failed to load your data. Please try logging in again.');
      setLoading(false);
      
      // If unauthorized, redirect to login
      if (error.response?.status === 401) {
        localStorage.removeItem('clientToken');
        navigate('/client-login');
      }
    }
  };

  const fetchProjectDetails = async (projectId) => {
    try {
      console.log(`Fetching details for project ${projectId}`);
      const response = await axios.get(`/api/client-portal/projects/${projectId}`);
      console.log('Project details response:', response.data);
      
      if (response.data && response.data.project) {
        const project = response.data.project;
        console.log(`Setting selected project with ${project.phases?.length || 0} phases`);
        
        if (project.phases) {
          project.phases.forEach((phase, index) => {
            console.log(`Phase ${index+1}: ${phase.name} with ${phase.steps?.length || 0} steps`);
          });
        }
        
        setSelectedProject(project);
      } else {
        console.error('No project data returned from API');
      }
    } catch (error) {
      console.error('Error fetching project details:', error);
      console.error('Error response:', error.response?.data);
      
      // If we already have some basic project info, maintain it but with empty phases
      if (selectedProject && selectedProject.id === projectId) {
        console.log('Keeping basic project info but with empty phases array');
        setSelectedProject(prev => ({
          ...prev,
          phases: []
        }));
      }
    }
  };
  
  const fetchStatusTypes = async () => {
    try {
      // Get phase status types
      const phaseResponse = await axios.get('/api/settings/status-types/phase');
      if (phaseResponse.data && phaseResponse.data.statusTypes) {
        setPhaseStatusTypes(phaseResponse.data.statusTypes);
      }
      
      // Get step status types
      const stepResponse = await axios.get('/api/settings/status-types/step');
      if (stepResponse.data && stepResponse.data.statusTypes) {
        setStepStatusTypes(stepResponse.data.statusTypes);
      }
    } catch (error) {
      console.error('Error fetching status types:', error);
      // Non-critical error, don't need to show to user
    }
  };
  
  const handleProjectSelect = async (project) => {
    // Set the basic project data immediately for responsiveness
    setSelectedProject(project);
    
    // Then fetch full details including phases and steps
    console.log(`Selected project: ${project.id} - ${project.name}`);
    await fetchProjectDetails(project.id);
  };

  const handleStepClick = (step) => {
    setSelectedStep(step);
  };

  const handleCloseStepModal = () => {
    setSelectedStep(null);
  };

  const handleFileUploaded = (file) => {
    // If we're viewing a step, update its files
    if (selectedStep) {
      setSelectedStep({
        ...selectedStep,
        files: [file, ...(selectedStep.files || [])]
      });
    }
    
    // Also fetch project details again to get updated files
    if (selectedProject?.id) {
      fetchProjectDetails(selectedProject.id);
    }
  };
  
  const handleLogout = () => {
    localStorage.removeItem('clientToken');
    navigate('/client-login');
  };
  
  // Open video modal
  const handleOpenVideo = (videoUrl, title) => {
    setCurrentVideo(videoUrl);
    setVideoTitle(title);
    setShowVideoModal(true);
  };
  
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-900">
        <div className="w-12 h-12 border-t-2 border-b-2 border-indigo-400 rounded-full animate-spin"></div>
        <p className="mt-4 text-gray-300">Loading your dashboard...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-900">
        <div className="w-full max-w-md p-8 space-y-4 bg-gray-800 rounded-lg shadow-md border border-gray-700">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-900">
              <svg className="w-6 h-6 text-red-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="mt-4 text-lg font-medium text-white">Error</h2>
            <p className="mt-2 text-sm text-gray-300">{error}</p>
            <button 
              onClick={() => navigate('/client-login')}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-900">
      {/* Modern Header */}
      <header className="bg-gray-800 shadow-md border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo and branding */}
            <div className="flex items-center">
              <h1 className="text-xl font-semibold text-white">Client Portal</h1>
              <div className="ml-3 px-3 py-1 rounded-md bg-indigo-900 text-indigo-100 text-sm">
                {clientData?.client?.name || 'Client'} 
              </div>
            </div>
            
            {/* User profile section */}
            <div className="flex items-center space-x-3">
              <div className="flex items-center">
                <div className="h-8 w-8 rounded-full bg-indigo-700 flex items-center justify-center text-indigo-100">
                  {clientData?.contact?.name ? clientData?.contact?.name.charAt(0).toUpperCase() : 'U'}
                </div>
                <span className="ml-2 text-sm text-gray-300">{clientData?.contact?.name || 'User'}</span>
              </div>
              <button
                onClick={handleLogout}
                className="text-sm px-3 py-1.5 rounded-md bg-gray-700 border border-gray-600 text-gray-300 hover:bg-gray-600 transition duration-150"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>
      
      {/* Project Selection Area */}
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Current Project Display */}
        {selectedProject && (
          <div className="bg-gray-800 rounded-lg shadow-md overflow-hidden mb-6 border border-gray-700">
            <div className="p-4">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-lg font-medium text-white">{selectedProject.name}</h2>
                  <div className="mt-1 flex items-center space-x-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                      ${selectedProject.status === 'Completed' ? 'bg-green-900 text-green-100' : 
                        selectedProject.status === 'In Progress' ? 'bg-blue-900 text-blue-100' : 
                        'bg-gray-700 text-gray-100'}`}>
                      {selectedProject.status}
                    </span>
                    {selectedProject.start_date && (
                      <span className="text-xs text-green-400">
                        Available: {new Date(selectedProject.start_date).toLocaleDateString()}
                      </span>
                    )}
                    {selectedProject.due_date && (
                      <span className="text-xs text-gray-400">
                        Due: {new Date(selectedProject.due_date).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
                
                {selectedProject.has_pending_missing_items && (
                  <div className="flex items-center justify-center px-3 py-1 rounded-md bg-red-900 text-red-100">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <span className="text-sm font-medium">
                      {selectedProject.pending_missing_items_count} Required {selectedProject.pending_missing_items_count === 1 ? 'Item' : 'Items'} Pending
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Project Selection */}
        <div className="mb-6">
          <label htmlFor="project-select" className="block text-sm font-medium text-gray-300 mb-1">
            Select Project
          </label>
          <div className="relative">
            <select
              id="project-select"
              value={selectedProject?.id ? selectedProject.id.toString() : ''}
              onChange={(e) => {
                const selectedId = e.target.value;
                const project = projects.find(p => p.id.toString() === selectedId);
                if (project) handleProjectSelect(project);
              }}
              className="block w-full rounded-md border-gray-600 bg-gray-700 text-white focus:border-indigo-500 focus:ring-indigo-500 shadow-sm py-2 pl-3 pr-10 text-base"
            >
              <option value="" disabled>Choose a project</option>
              {projects.map(project => (
                <option 
                  key={project.id} 
                  value={project.id.toString()}
                >
                  {project.name} {project.has_pending_missing_items ? '(⚠️)' : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Project Cards */}
        <div className="bg-gray-800 rounded-lg shadow-md p-5 mb-8 border border-gray-700">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-base font-medium text-white">Your Projects</h3>
            <button 
              onClick={() => setShowAllProjects(!showAllProjects)}
              className="text-sm text-indigo-400 hover:text-indigo-300 flex items-center"
            >
              {showAllProjects ? (
                <>
                  <span>Show Less</span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                  </svg>
                </>
              ) : (
                <>
                  <span>Show All Projects</span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </>
              )}
            </button>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Show either all projects or just projects with pending items */}
            {(showAllProjects ? projects : projects.filter(p => p.has_pending_missing_items)).slice(0, showAllProjects ? undefined : 3).map(project => (
              <div 
                key={project.id}
                onClick={() => handleProjectSelect(project)}
                className={`relative rounded-lg cursor-pointer transition-all duration-150 overflow-hidden ${
                  selectedProject?.id === project.id ? 'ring-2 ring-indigo-500 bg-indigo-900' : 'border border-gray-600 hover:border-indigo-500 hover:bg-gray-700'
                }`}
              >
                {/* Color bar at top based on status */}
                <div className={`h-1 w-full ${
                  project.status === 'Completed' ? 'bg-green-500' : 
                  project.status === 'In Progress' ? 'bg-blue-500' : 
                  project.has_pending_missing_items ? 'bg-amber-500' : 'bg-gray-500'
                }`}></div>
                
                <div className="p-4">
                  <div className="flex justify-between">
                    <h4 className="text-sm font-medium text-white truncate">{project.name}</h4>
                    
                    {project.has_pending_missing_items && (
                      <div className="flex-shrink-0">
                        <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-red-100 bg-red-600 rounded-full">
                          {project.pending_missing_items_count}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-2 flex flex-col space-y-1">
                    <div className="flex justify-between items-center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                        ${project.status === 'Completed' ? 'bg-green-900 text-green-100' : 
                          project.status === 'In Progress' ? 'bg-blue-900 text-blue-100' : 
                          'bg-gray-700 text-gray-100'}`}>
                        {project.status}
                      </span>
                    </div>
                    {project.start_date && (
                      <span className="text-xs text-green-400">
                        Available: {new Date(project.start_date).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {!showAllProjects && projects.filter(p => p.has_pending_missing_items).length > 3 && (
            <div className="mt-4 text-center">
              <button 
                onClick={() => setShowAllProjects(true)}
                className="inline-flex items-center px-3 py-1.5 border border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-300 bg-gray-700 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                View {projects.filter(p => p.has_pending_missing_items).length - 3} more with pending items
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Project Content */}
      {selectedProject && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          {/* Improved Tab Navigation */}
          <div className="bg-gray-800 rounded-lg shadow-md mb-6 overflow-hidden border border-gray-700">
            <div className="border-b border-gray-700">
              <nav className="flex px-4 -mb-px" aria-label="Tabs">
                <button
                  onClick={() => setActiveTab('timeline')}
                  className={`${
                    activeTab === 'timeline'
                      ? 'border-indigo-500 text-indigo-400 font-medium'
                      : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-500'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center mr-8`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Timeline
                </button>
                
                <button
                  onClick={() => setActiveTab('plans')}
                  className={`${
                    activeTab === 'plans'
                      ? 'border-indigo-500 text-indigo-400 font-medium'
                      : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-500'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center mr-8`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  Project Plans
                </button>
                
                <button
                  onClick={() => setActiveTab('upload')}
                  className={`${
                    activeTab === 'upload'
                      ? 'border-indigo-500 text-indigo-400 font-medium'
                      : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-500'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center mr-8`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  Upload Files
                </button>
                
                <button
                  onClick={() => setActiveTab('updates')}
                  className={`${
                    activeTab === 'updates'
                      ? 'border-indigo-500 text-indigo-400 font-medium'
                      : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-500'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center mr-8`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                  </svg>
                  Project Updates
                </button>
                
                <button
                  onClick={() => setActiveTab('missing')}
                  className={`${
                    activeTab === 'missing'
                      ? 'border-indigo-500 text-indigo-400 font-medium'
                      : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-500'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center relative`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Required Items
                  {selectedProject?.has_pending_missing_items && (
                    <span className={`absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-xs text-white`}>
                      {selectedProject.pending_missing_items_count}
                    </span>
                  )}
                </button>
              </nav>
            </div>
          </div>
          
          {/* Tab Content */}
          <div className="bg-gray-800 rounded-lg shadow-md p-5 border border-gray-700">
            {/* Timeline Tab */}
            {activeTab === 'timeline' && (
              <div className="space-y-6">
                {/* Due Dates Summary Card */}
                <div className="rounded-lg bg-gray-700 p-4 mb-6 border border-gray-600">
                  <h3 className="text-lg font-medium text-indigo-300 mb-2 flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Upcoming Due Dates
                  </h3>
                  <p className="text-sm text-indigo-100 mb-4">
                    Timeline of upcoming project, phase, and step deadlines.
                  </p>
                  
                  <DueDatesSummary project={selectedProject} />
                </div>
                
                {/* Project Phases and Steps */}
                <div>
                  {selectedProject.phases && selectedProject.phases.map((phase, phaseIndex) => (
                    <div key={phase.id} className={`mb-5 bg-gray-800 rounded-lg border border-gray-600 shadow-md overflow-hidden`}>
                      {/* Phase Header */}
                      <div className="p-4 bg-gray-700 border-b border-gray-600">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-7 w-7 flex items-center justify-center bg-indigo-800 rounded-full text-sm font-medium text-indigo-100">
                              {phaseIndex + 1}
                            </div>
                            <h3 className="ml-2 text-lg font-medium text-white">{phase.name}</h3>
                            <div className="ml-2 px-2 py-0.5 inline-flex text-xs leading-5 font-medium rounded-full bg-gray-600 text-gray-100">
                              {phase.status || 'Not Started'}
                            </div>
                          </div>
                          
                          {/* Show video button if available */}
                          {phase.video_url && (
                            <button 
                              onClick={() => handleOpenVideo(phase.video_url, `Phase: ${phase.name}`)}
                              className="inline-flex items-center px-2.5 py-1.5 text-sm text-indigo-300 bg-indigo-900 rounded-md hover:bg-indigo-800 transition-colors"
                            >
                              <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              Watch Instructions
                            </button>
                          )}
                        </div>
                        {phase.description && (
                          <p className="mt-1 text-sm text-gray-400 ml-9">{phase.description}</p>
                        )}
                      </div>
                      
                      {/* Steps */}
                      <div>
                        {phase.steps && phase.steps.map((step, stepIndex) => (
                          <div 
                            key={step.id} 
                            className="p-4 hover:bg-gray-700 cursor-pointer border-b border-gray-600 last:border-b-0 transition-colors"
                            onClick={() => handleStepClick(step)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                <div className="mr-3 flex-shrink-0">
                                  {step.status === 'Completed' ? (
                                    <div className="h-6 w-6 rounded-full bg-green-900 flex items-center justify-center">
                                      <svg className="h-4 w-4 text-green-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                      </svg>
                                    </div>
                                  ) : step.status === 'In Progress' ? (
                                    <div className="h-6 w-6 rounded-full bg-yellow-900 flex items-center justify-center">
                                      <svg className="h-4 w-4 text-yellow-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                      </svg>
                                    </div>
                                  ) : (
                                    <div className="h-6 w-6 rounded-full bg-gray-600 flex items-center justify-center">
                                      <span className="text-xs font-medium text-gray-300">{stepIndex + 1}</span>
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <span className="text-sm font-medium text-gray-200">{step.name}</span>
                                  {step.upload_request && (
                                    <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-900 text-blue-200">
                                      Upload Required
                                    </span>
                                  )}
                                </div>
                                {/* Make video icon clickable and stop propagation */}
                                {step.video_url && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenVideo(step.video_url, `Step: ${step.name}`);
                                    }}
                                    className="ml-2 flex items-center text-indigo-400 hover:text-indigo-300"
                                  >
                                    <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                    </svg>
                                    <span className="text-xs">Watch</span>
                                  </button>
                                )}
                              </div>
                              <div className="flex items-center space-x-2">
                                {step.due_date && (
                                  <span className={`text-xs px-2 py-1 rounded-md ${
                                    new Date(step.due_date) < new Date() ? 'bg-red-900 text-red-200' :
                                    new Date(step.due_date) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) ? 'bg-yellow-900 text-yellow-200' :
                                    'bg-green-900 text-green-200'
                                  }`}>
                                    Due: {new Date(step.due_date).toLocaleDateString()}
                                  </span>
                                )}
                                <span className="text-xs font-medium text-indigo-400 hover:text-indigo-300 flex items-center">
                                  View Details
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                        {(!phase.steps || phase.steps.length === 0) && (
                          <div className="p-4 text-sm text-gray-400 italic">No steps in this phase.</div>
                        )}
                      </div>
                    </div>
                  ))}
                  {(!selectedProject.phases || selectedProject.phases.length === 0) && (
                    <div className="p-4 text-sm text-gray-400 italic">No phases defined for this project.</div>
                  )}
                </div>
              </div>
            )}
            
            {/* Project Plans Tab */}
            {activeTab === 'plans' && (
              <ClientPortalProjectPlan 
                projectId={selectedProject.id} 
                clientId={clientData?.client?.id} 
              />
            )}
            
            {/* Upload Files Tab */}
            {activeTab === 'upload' && (
              <CentralFileUploader 
                projectId={selectedProject.id} 
                onFileUploaded={handleFileUploaded} 
              />
            )}
            
            {/* Project Updates Tab */}
            {activeTab === 'updates' && (
              <ClientPortalNotes projectId={selectedProject.id} />
            )}
            
            {/* Required Items Tab */}
            {activeTab === 'missing' && (
              <ClientPortalMissingItems projectId={selectedProject.id} />
            )}
          </div>
        </div>
      )}
      
      {/* Step Details Modal */}
      {selectedStep && (
        <StepDetailsModal
          step={selectedStep}
          onClose={handleCloseStepModal}
          onFileUploaded={handleFileUploaded}
          darkMode={true}
        />
      )}
      
      {/* Video Modal */}
      {showVideoModal && currentVideo && (
        <VideoModal
          isOpen={showVideoModal}
          onClose={() => setShowVideoModal(false)}
          videoUrl={currentVideo}
          title={videoTitle}
          darkMode={true}
        />
      )}
    </div>
  );
};

export default ClientDashboard; 